import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import publicAxiosInstance from "../services/publicApi";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for Fetching All Customers
export const fetchAllCustomers = createAsyncThunk(
  "customer/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.CUSTOMER.SELECT_ALL,
      );
      return response.data;
    } catch (error) {
      console.error("fetchCustomers error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customers",
      );
    }
  },
);

// Thunk for Fetching Pending Customers
export const fetchPendingCustomers = createAsyncThunk(
  "customer/fetchPending",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.CUSTOMER.PENDING);
      return response.data;
    } catch (error) {
      console.error("fetchPendingCustomers error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending customers",
      );
    }
  },
);

// Thunk for Approving a Customer
export const approveCustomer = createAsyncThunk(
  "customer/approve",
  async (customerId, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.CUSTOMER.APPROVE, {
        customerId,
      });
      // Refresh pending list
      dispatch(fetchPendingCustomers());
      dispatch(fetchAllCustomers());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve customer",
      );
    }
  },
);

// Thunk for Rejecting a Customer
export const rejectCustomer = createAsyncThunk(
  "customer/reject",
  async ({ customerId, rejectionReason }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.CUSTOMER.REJECT, {
        customerId,
        rejectionReason,
      });
      // Refresh pending list
      dispatch(fetchPendingCustomers());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to reject customer",
      );
    }
  },
);

// Thunk for Fetching Customer Feedbacks
export const fetchCustomerFeedbacks = createAsyncThunk(
  "customer/fetchFeedbacks",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.CUSTOMER.FEEDBACK_SELECT_ALL,
      );
      return response.data;
    } catch (error) {
      console.error("fetchCustomerFeedbacks error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer feedbacks",
      );
    }
  },
);

// Thunk for Staff Registering a Customer
export const registerCustomer = createAsyncThunk(
  "customer/register",
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.CUSTOMER.REGISTER,
        customerData,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to register customer",
      );
    }
  },
);

// Thunk for Self-Registering a Customer (Public) - using public instance
export const selfRegisterCustomer = createAsyncThunk(
  "customer/selfRegister",
  async (customerData, { rejectWithValue }) => {
    try {
      const response = await publicAxiosInstance.post(
        API_ENDPOINTS.CUSTOMER.SELF_REGISTER,
        customerData,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Self-Register Error:",
        error.response?.status || "No Response",
        error.response?.data || error.message,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to self-register",
      );
    }
  },
);

// Thunk for Submitting Customer Feedback (Public)
export const submitCustomerFeedback = createAsyncThunk(
  "customer/submitFeedback",
  async (feedbackData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.CUSTOMER.SUBMIT,
        feedbackData,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to submit feedback",
      );
    }
  },
);

// Thunk for Fetching Single Customer By ID
export const fetchCustomerById = createAsyncThunk(
  "customer/fetchById",
  async (customerId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.CUSTOMER.SELECT}?id=${customerId}`,
      );
      return response.data;
    } catch (error) {
      console.error("fetchCustomerById error:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer details",
      );
    }
  },
);

const customerSlice = createSlice({
  name: "customer",
  initialState: {
    customersList: [],
    pendingCustomers: [],
    feedbacksList: [],
    isLoading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearCustomerState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.customersList = apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchAllCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Pending
      .addCase(fetchPendingCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPendingCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.pendingCustomers =
          apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchPendingCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Feedbacks
      .addCase(fetchCustomerFeedbacks.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerFeedbacks.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.feedbacksList = apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchCustomerFeedbacks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Approve/Reject
      .addCase(approveCustomer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(approveCustomer.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(approveCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(rejectCustomer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(rejectCustomer.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(rejectCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(registerCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message || "Customer successfully registered!";
      })
      .addCase(registerCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Self Register
      .addCase(selfRegisterCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(selfRegisterCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message ||
          "Registration successful! Your request is pending approval.";
        // Note: Auto-refresh can be triggered by parent component or manually
      })
      .addCase(selfRegisterCustomer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Submit Feedback
      .addCase(submitCustomerFeedback.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(submitCustomerFeedback.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message || "Feedback submitted successfully!";
      })
      .addCase(submitCustomerFeedback.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch By ID
      .addCase(fetchCustomerById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.selectedCustomer =
          apiData?.data || apiData || action.payload || null;
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCustomerState } = customerSlice.actions;
export default customerSlice.reducer;
